import { PublicationStatusSchema } from "../solo-cms/publication-status";
import { CampaignVisibilitySchema } from "../trade-to-earn/trade-to-earn-campaign.dto";
import { HteRewardDistributionSchema } from "./hold-to-earn.dto";
import { z } from "zod";

export const HoldingEpochDtoSchema = z
  .object({
    start: z.string(),
    end: z.string(),
    isPaused: z.boolean(),
    isPausedFrom: z.string().nullable(),
    production: z.boolean(),
    rewardPoolAmount: z.number().positive(),
    numberOfSpots: z.number().int().positive().nullable(),
    minimumUsdBalance: z.number().nullable(),
    exchangeIds: z.array(z.string()),
    rewardRule: z.string(),
    duration: z.number().positive(),
    onlyHubUserWithNoTradeEventsBefore: z.string().nullable(),
    // Defaults cover epochs saved before the toggle existed.
    enableProportionalTotalReward: z.boolean().default(false),
    /** Token amount held across all users that unlocks the full pool of each epoch. */
    buyInAmountTarget: z.number().positive().nullable().default(null),
  })
  .superRefine((epoch, ctx) => {
    if (!epoch.enableProportionalTotalReward) return;
    if (epoch.rewardRule !== "proportional") {
      ctx.addIssue({
        code: "custom",
        path: ["enableProportionalTotalReward"],
        message: "Proportional Total Reward is only available with the Proportional reward rule",
      });
    }
    if (epoch.buyInAmountTarget == null) {
      ctx.addIssue({
        code: "custom",
        path: ["buyInAmountTarget"],
        message: "Epoch Buy in Amount Target is required when Proportional Total Reward is enabled",
      });
    }
  });

export type HoldingEpochDto = z.infer<typeof HoldingEpochDtoSchema>;

export const SaveHteCampaignDtoSchema = z.object({
  id: z.uuid().optional(),
  name: z.string().min(1, "Name is required"),
  title: z.string().min(1, "Title is required"),
  shortDescription: z.string().min(1, "Short description is required"),
  tokenSymbol: z.string().min(1, "Token symbol is required"),
  rewardToken: z.string().min(1, "Reward token is required"),
  startAt: z.coerce.date(),
  endAt: z.coerce.date(),
  production: z.boolean(),
  visibility: CampaignVisibilitySchema,
  privateCampaignPartner: z.string().optional().nullable(),
  privateCampaignUtm: z.string().optional().nullable(),
  privateCampaignUtms: z.array(z.string()).optional().default([]),
  /** Required for a classic campaign; a Merkl campaign has no vault and the API stores a placeholder. */
  rewardVaultAddressProduction: z.string().optional().nullable(),
  rewardVaultAddressDevelopment: z.string().optional().nullable(),
  rewardVaultAddressStaging: z.string().optional().nullable(),
  rewardDistribution: HteRewardDistributionSchema,
  /** The Merkl campaign paying a `merkl` campaign: unknown until it is created on Merkl. */
  merklCampaignId: z
    .string()
    .regex(/^0x[0-9a-fA-F]{64}$/, "Merkl campaign id must be a 32-byte hex string")
    .optional()
    .nullable(),
  uniqueParticipationByUser: z.boolean(),
  requireTelegramVerification: z.boolean(),
  requireDiscordVerification: z.boolean(),
  requireTwitterVerification: z.boolean(),
  enableTopHolder: z.boolean(),
  topHolderRewardCount: z.number().int().nonnegative(),
  icon: z.string().optional().nullable(),
  tokenIcon: z.string().optional().nullable(),
  networkReward: z.array(z.string()),
  descriptions: z.array(
    z.object({
      title: z.string(),
      content: z.string(),
    }),
  ),
  holdingEpochs: z.array(HoldingEpochDtoSchema),
  campaignPartnerNames: z.array(z.string()),
  publicationStatus: PublicationStatusSchema,
});

export type SaveHteCampaignDto = z.infer<typeof SaveHteCampaignDtoSchema>;

export const HteCampaignPreviewDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string(),
  tokenSymbol: z.string(),
  rewardToken: z.string(),
  production: z.boolean(),
  publicationStatus: PublicationStatusSchema,
  visibility: CampaignVisibilitySchema,
  startAt: z.coerce.date(),
  endAt: z.coerce.date(),
  networkReward: z.array(z.string()),
  exchangeIds: z.array(z.string()),
});

export type HteCampaignPreviewDto = z.infer<typeof HteCampaignPreviewDtoSchema>;

export const HteCampaignPreviewsListResponseSchema = z.object({
  campaigns: z.array(HteCampaignPreviewDtoSchema),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
});

export type HteCampaignPreviewsListResponse = z.infer<typeof HteCampaignPreviewsListResponseSchema>;

export const HteCampaignFullDataDtoSchema = SaveHteCampaignDtoSchema.extend({
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type HteCampaignFullDataDto = z.infer<typeof HteCampaignFullDataDtoSchema>;
